import type { ReactNode } from 'react';

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  /** Tailwind classes appended to the <td> for this column. */
  className?: string;
  /** Width hint applied to the <th> + <td> for this column. */
  width?: string;
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  /** Rendered when `data.length === 0`. */
  emptyState?: ReactNode;
}

/**
 * Minimal generic table. Header + rows. No sorting / pagination /
 * selection yet — those land per-resource as needed (most lists in v1
 * are small enough that client-side filtering is sufficient).
 */
export function DataTable<T>({
  data,
  columns,
  rowKey,
  emptyState,
}: DataTableProps<T>): JSX.Element {
  if (data.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }
  return (
    <div className="overflow-x-auto rounded border border-border">
      <table className="w-full text-sm">
        <thead className="bg-surface-alt text-ink-muted">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className="px-3 py-2 text-left font-medium"
                style={col.width ? { width: col.width } : undefined}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border bg-surface">
          {data.map((row) => (
            <tr key={rowKey(row)} className="hover:bg-surface-alt">
              {columns.map((col) => (
                <td key={col.key} className={`px-3 py-2 align-middle ${col.className ?? ''}`}>
                  {col.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
